/**
 * NexusTicker — Bloomberg-style horizontal scroll bar for Shura Nexus.
 *
 * Polls `GET /api/insights/ticker` every 20s (also re-fetches when the
 * window refocuses) and renders a CSS-marquee strip of recent
 * ecosystem events:
 *
 *   ⚡ MERIDIAN VENT joined ↗ HEALTHTECH · SEED
 *   ◇ LINK 47 score 92
 *   ⚠ YUSUF computed RISK · HIGH · 78
 *   $ ATLAS CAPITAL → ZEUS WORKS · TERM SHEET · $4.2M
 *   ✓ AGREEMENT signed · NDA · DRAFT
 *
 * Pure CSS animation (no JS rAF loop) — fully GPU-accelerated and
 * pauses on hover so operators can read.  Theme-aware via the existing
 * `.pal-*` utility set.
 */
import React, { useEffect, useState } from 'react';
import { Rocket, User, Building2, ArrowUpRight, ShieldCheck, Activity, Sparkles } from 'lucide-react';
import api from '@/lib/api';

const ICON_MAP = {
  rocket: Rocket,
  user: User,
  building: Building2,
  arrow: ArrowUpRight,
  shield: ShieldCheck,
  pulse: Activity,
};

function relTime(iso) {
  if (!iso) return null;
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (!isFinite(diff)) return null;
  if (diff < 0) return 'now';
  if (diff < 60) return `${Math.round(diff)}s`;
  if (diff < 3600) return `${Math.round(diff / 60)}m`;
  if (diff < 86400) return `${Math.round(diff / 3600)}h`;
  return `${Math.round(diff / 86400)}d`;
}

export default function NexusTicker() {
  const [events, setEvents] = useState([]);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const fetchTicker = async () => {
      try {
        const { data } = await api.get('/insights/ticker', { params: { since_seconds: 86400, limit: 30 } });
        if (!cancelled) setEvents(data?.events || []);
      } catch {
        /* silent — never block the header on a failed ticker call */
      }
    };
    fetchTicker();
    const t = setInterval(fetchTicker, 20_000);
    const focus = () => fetchTicker();
    window.addEventListener('focus', focus);

    // Hybrid mode: also subscribe to the live `/api/events/ws` bus so the
    // ticker updates the instant any event broadcasts (booking created,
    // shura recompute, etc.) without waiting for the 20s poll cycle.
    // We bail out silently if the WS can't open — polling stays as the
    // baseline source of truth.
    let ws = null;
    let wsHeartbeat = null;
    try {
      // Cookie-first WS auth (post-c.2). Browsers send the HttpOnly
      // session_token cookie automatically on same-origin handshakes.
      const wsUrl = (process.env.REACT_APP_BACKEND_URL || '')
        .replace(/^https?:\/\//, (m) => (m === 'https://' ? 'wss://' : 'ws://'))
        + `/api/events/ws`;
      ws = new WebSocket(wsUrl);
      ws.onmessage = (e) => {
          try {
            const msg = JSON.parse(e.data);
            // Refresh on any non-hello/error/pong frame — broadcast frames
            // carry a `type` like `booking.created`, `shura.recompute`,
            // `risk_pulse.computed`, etc.
            if (msg?.type && !['hello', 'error', 'pong'].includes(msg.type)) {
              fetchTicker();
            }
          } catch { /* ignore */ }
        };
      ws.onopen = () => {
          // 30s ping to keep the socket warm
          wsHeartbeat = setInterval(() => {
            try { ws.send('ping'); } catch { /* ignore */ }
          }, 30_000);
        };
      ws.onerror = () => { /* silent */ };
      ws.onclose = () => { if (wsHeartbeat) clearInterval(wsHeartbeat); };
    } catch { /* silent */ }

    return () => {
      cancelled = true;
      clearInterval(t);
      window.removeEventListener('focus', focus);
      if (ws) try { ws.close(); } catch { /* ignore */ }
      if (wsHeartbeat) clearInterval(wsHeartbeat);
    };
  }, []);

  if (!events.length) return null;

  // Duplicate the list so the marquee loops seamlessly.
  const doubled = [...events, ...events];

  return (
    <div
      className="pal-card pal-card-live overflow-hidden relative"
      data-testid="shura-ticker"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {/* Left status pill */}
      <div className="absolute left-0 top-0 bottom-0 z-10 px-3 flex items-center gap-1.5 bg-gradient-to-r from-[var(--ve-black,#0A0A0A)] via-[var(--ve-black,#0A0A0A)] to-transparent dark:from-black dark:via-black/95">
        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
        <span className="pal-bracket text-[9px]">[NEXUS · LIVE]</span>
      </div>
      {/* Right fade-out gradient */}
      <div className="absolute right-0 top-0 bottom-0 z-10 w-14 bg-gradient-to-l from-[var(--ve-black,#0A0A0A)] via-[var(--ve-black,#0A0A0A)]/70 to-transparent pointer-events-none dark:from-black" />

      {/* Marquee strip */}
      <div
        className={`flex gap-6 items-center py-2.5 pl-[140px] whitespace-nowrap nexus-ticker-marquee ${
          paused ? 'paused' : ''
        }`}
      >
        {doubled.map((e, i) => {
          const Icon = ICON_MAP[e.icon] || Sparkles;
          const age = relTime(e.ts);
          return (
            <div
              key={`${e.label}-${i}-${e.kind}`}
              className="flex items-center gap-2 shrink-0 pl-3 first:pl-0"
              style={{ borderLeft: i === 0 ? 'none' : '1px solid rgba(174,82,234,0.18)' }}
            >
              <Icon size={11} style={{ color: e.accent, filter: `drop-shadow(0 0 6px ${e.accent}88)` }} />
              <span className="pal-mono text-[11px] font-bold tracking-wide" style={{ color: e.accent }}>
                {e.label.toUpperCase()}
              </span>
              <span className="pal-mono text-[10px] opacity-65">·</span>
              <span className="pal-mono text-[10px] opacity-85">{e.detail}</span>
              {age && (
                <>
                  <span className="pal-mono text-[10px] opacity-40">·</span>
                  <span className="pal-mono text-[10px] opacity-50">{age}</span>
                </>
              )}
            </div>
          );
        })}
      </div>

      <style>{`
        .nexus-ticker-marquee {
          animation: nexus-ticker-scroll 95s linear infinite;
          will-change: transform;
        }
        .nexus-ticker-marquee.paused {
          animation-play-state: paused;
        }
        @keyframes nexus-ticker-scroll {
          from { transform: translateX(0); }
          to   { transform: translateX(-50%); }
        }
        @media (prefers-reduced-motion: reduce) {
          .nexus-ticker-marquee { animation: none; }
        }
      `}</style>
    </div>
  );
}
